/**
 * v0.4 追加要件⑱ D-439: ナビゲーション定義の単一ソース。
 *
 * ⚠️⚠️ 従来 `components/AppLayout.tsx` の `navGroups` と `pages/HomePage.tsx` の `menuCards` が
 *  同じ約50項目を別スキーマで二重に保持しており、**§8-55 (片方だけ更新して同期が漏れる) が
 *  繰り返し発生する構造的な発生源**になっていた (CLAUDE.md §8-55 は「3並列レビュー5ラウンドで
 *  検出できなかった」実例を記録している)。本ファイルへ一本化することで、その面を構造的に消す。
 *  `docs/design/ui-guidelines.md` §8 の「ナビの再掲をコンテンツにしない」にも整合する。
 *
 * ⚠️ **新しい画面を足すときは、ここに1行足すだけでサイドバーとホームの両方に出る。**
 *  ホームに出したくない項目は `hideInHome: true`、説明文は `description` に書く
 *  (`description` はホームのカードだけが使い、サイドバーでは使わない)。
 *
 * ⚠️ permission 側の登録箇所は別途5つある (§8-143): `permissions` / `role_permissions` の grant /
 *  `STATIC_*_PERMISSIONS` / `routes/roles.ts` の `V03_FEATURES` / `lib/feature-flags.ts` の
 *  `FEATURE_FLAG_REFS`。**本ファイルはあくまで「画面に出すか」だけを決める。**
 *
 * 設計: docs/design/v04-navigation-ia-restructure.md (D-438〜D-445)
 */
import {
  LayoutDashboard, Upload, Database, Users, TrendingUp, GitCompare, ShieldCheck,
  Home, Truck, Building2, UtensilsCrossed, FolderTree, AlertTriangle,
  DatabaseBackup, Wallet, Receipt, Bike, PackageCheck, ClipboardList, Wheat, FileScan,
  Sparkles, Star, ShieldAlert, ChefHat, Settings, IdCard, CheckCircle2, FileSpreadsheet,
  PenLine, History, CalendarDays, CalendarClock, CalendarCheck, GraduationCap, PartyPopper,
  Repeat, PiggyBank, Printer, Umbrella, HeartPulse, FileText, Table2, UserSearch, Scale, Target, BookOpen,
  CalendarPlus, UsersRound, ClipboardCheck, Users2, MapPinned, Gauge, FlaskConical, Coins, Rocket,
  Award, Hourglass, ListChecks, Newspaper,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { LabelKey } from '@/lib/label-keys';
import { hasPermission, hasNonSelfPermission, type CurrentUser } from '@/stores/auth';

export type NavGroupKey =
  | 'home' | 'my' | 'dashboard' | 'shift' | 'labor' | 'talent' | 'data'
  | 'master' | 'menu_dev' | 'board' | 'admin' | 'system';

/** ホーム画面がカードのグループ見出し・配色に使うキー (`home`/`system` はホームに出さない) */
export type HomeGroupKey = Exclude<NavGroupKey, 'home' | 'system'>;

export type NavEntry = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** ホーム画面のカードに出す説明文。サイドバーでは使わない */
  description?: string;
  /** true の場合、label の「全社」固定文言を useRootLabel() の動的な値に置換して表示する */
  dynamicRootLabel?: boolean;
  /** ホーム画面には出さない (ホーム自身へのリンク・システム管理者機能) */
  hideInHome?: boolean;
  /** サイドバーではグループ内ではなく指定スロットに描画する。'config' = スクロール領域外の最下部固定 */
  sidebarSlot?: 'config';

  // ---- 表示ゲート (canShowNavEntry が解釈する。増やすときは同関数も必ず更新すること) ----
  /** admin_required=true は company_admin/platform_admin のみ表示。
   *
   *  ⚠️⚠️ **requiredFeature / requiredAnyFeature が指定された項目では評価されない。これは
   *  バグではなく v0.3 カスタムロールの意図的な設計である** (`docs/design/v03-custom-roles.md` §235-236:
   *  「canShowNavItem を『admin_required=true なら admin』から『requiredFeature の保有』へ**置換**」
   *  「既存の admin_required は**互換のため残置** (未定義 feature の項目は従来通り admin 判定)」)。
   *
   *  ⚠️⚠️ **AND 条件に「直して」はならない。**そうするとロールベースの判定が permission ベースの
   *  上に復活し、**カスタムロール機能そのものを無効化する** — テナントが独自ロールに
   *  `import:manage` を付与しても、company_admin でなければナビに出なくなる。 */
  admin_required?: boolean;
  /** 指定時、admin_required より優先して effective_permissions の保有で判定する */
  requiredFeature?: string;
  /** requiredFeature / requiredAnyFeature をどの action で判定するか (既定 'view')。
   *  ⚠️ v0.4 追加要件⑱ D-440: 書込み専用の画面は view で出すと「開けるが保存できない」dead-end になる。
   *  **バックエンドが実際に要求する action を実測してから指定すること** (推測で付けない)。 */
  requiredAction?: 'view' | 'create' | 'manage';
  /** true の場合、`scope='self'` の permission だけを持つユーザーには表示しない (D-440/D-447)。
   *
   *  ⚠️⚠️ **「minScope」という名前にしなかった理由**: `PERMISSION_SCOPES`
   *  (`platform | all_stores | multi_store | own_store | node_scope | self`) は
   *  **広さの全順序ではない** (`node_scope` と `own_store` は互いに比較できない)。
   *  順序を仮定した `minScope` を作ると、誤った比較を後から書き込まれる面ができる。
   *  実際に必要なのは「自分の分しか見られない人に、他人のデータを扱う画面を出さない」ことだけ。
   *
   *  ⚠️⚠️ **付けてよいのは「self scope では実際にバックエンドが 403 を返す画面」だけ**。
   *  self を明示的にサポートする画面 (自分の行だけ返す等) に付けると**正当な利用を壊す**。
   *  D-447 の実測表を必ず参照すること。 */
  requiresNonSelfScope?: boolean;
  /** 複数 feature のいずれか1つでも view を持てば表示する。requiredFeature より優先 */
  requiredAnyFeature?: string[];
  /** 権限に加えてテナント feature flag の ON/OFF でも判定する */
  requiredTenantFeature?: 'publish_gate_enabled' | 'shift_report' | 'overtime_compliance' | 'document_library'
    | 'leave_management' | 'family_member_management' | 'competency_management' | 'placement_constraint'
    | 'development_plan' | 'board_meeting' | 'ai_usage_management' | 'attendance_monthly_v2';
  /** Lite テナントでは非表示 (Full 専用テーブル依存画面の 500 予防、§8-70 と同型の事故予防) */
  fullOnly?: boolean;
  /** Full テナントでは非表示 (tenant-lite migration にのみ存在するテーブル依存画面) */
  liteOnly?: boolean;
  /** テナント内の権限モデルとは無関係に is_cross_tenant_admin のユーザーにのみ表示する */
  platformAdminOnly?: boolean;
};

export type NavGroup = {
  label: string;
  /** グループ名がテナント別にカスタマイズ可能な場合は label_key_catalog のキーを指定する */
  labelKey?: LabelKey;
  key: NavGroupKey;
  /** ホーム画面のカード一覧にこのグループを出すか */
  showInHome: boolean;
  items: NavEntry[];
};

/**
 * 表示可否の判定。サイドバー・ホームの両方がこの1本を使う。
 *
 * ⚠️ 一本化にあたっての振る舞いの注意: 従来 `HomePage.canShowCard` は `platformAdminOnly` を
 *  見ていなかったが、`platformAdminOnly` を持つ項目 (`/platform`) は `hideInHome: true` で
 *  ホームの対象から先に除かれるため、**判定を統合しても表示結果は変わらない**。
 */
export const canShowNavEntry = (
  entry: NavEntry,
  user: CurrentUser | null | undefined,
  isAdmin: boolean,
): boolean => {
  if (entry.platformAdminOnly) return user?.is_cross_tenant_admin === true;
  const isLiteEdition = user?.edition === 'lite';
  if (entry.fullOnly && isLiteEdition) return false;
  if (entry.liteOnly && !isLiteEdition) return false;
  if (entry.requiredTenantFeature && user?.tenant_features?.[entry.requiredTenantFeature] === false) return false;
  // D-440: 既定は 'view'。書込み専用画面は requiredAction で実際に必要な action を指定する。
  const action = entry.requiredAction ?? 'view';
  // D-440/D-447: self scope しか持たないユーザーを弾く。
  // ⚠️ `scope` が undefined の permission は「self ではない」として扱う (= 弾かない)。
  //    理由: `scope` は D-446 で追加された新しいフィールドであり、古いレスポンスを保持した
  //    クライアントでは undefined になりうる。ここで fail-safe (隠す) に倒すと、
  //    **API 更新前のクライアントで画面が突然消える**。表示制御は認可ではなく (実際の認可は
  //    常にサーバー側)、隠しすぎる方が実害が大きいため、意図的に fail-open にしている。
  if (entry.requiresNonSelfScope) {
    // v0.4 (休暇管理 TD-26 積み残し②レビュー指摘): 判定本体は stores/auth.ts の
    // hasNonSelfPermission() へ集約 (EmployeeDetailPage.tsx の家族情報タブも同じ関数を使う)。
    const featureList = entry.requiredAnyFeature ?? (entry.requiredFeature ? [entry.requiredFeature] : []);
    if (!hasNonSelfPermission(user?.effective_permissions, featureList)) return false;
  }
  if (entry.requiredAnyFeature) {
    return entry.requiredAnyFeature.some(
      (f) => hasPermission(user?.effective_permissions, f, action, user?.non_inheriting_features),
    );
  }
  if (entry.requiredFeature) {
    return hasPermission(user?.effective_permissions, entry.requiredFeature, action, user?.non_inheriting_features);
  }
  return !entry.admin_required || isAdmin;
};

/** ナビ全体の定義。サイドバーはこの順・このグループで描画し、ホームは showInHome のグループのみ描画する。 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'ホーム', key: 'home', showInHome: false,
    items: [
      { to: '/', label: 'ホーム', icon: Home, hideInHome: true },
    ],
  },
  {
    label: 'マイページ', key: 'my', showInHome: true,
    items: [
      { to: '/shift/my', label: '自分のシフト', description: '確定した自分の勤務日・時間帯を確認', icon: CalendarCheck, requiredFeature: 'shift_my', fullOnly: true },
      { to: '/shift/preferences', label: 'シフト希望', description: '勤務可能な日時の希望を提出', icon: CalendarClock, requiredFeature: 'shift_preference', fullOnly: true },
      // v0.5① 日報機能 増分1 (§14 P-1): 両edition対応 (§14-2表)。⚠️ daily_report は tenant_features に
      // 未登録 (未実装) のため requiredTenantFeature は付けない (denomination-masters と同型、:388参照)。
      { to: '/daily-reports', label: '日報', description: '日々の売上・現金・レジ締め等を記録', icon: ClipboardList, requiredFeature: 'daily_report' },
      { to: '/me/income', label: '自分の年収残枠', description: '当社分の年収累計・見込みを確認 (「年収の壁」対策)', icon: PiggyBank, requiredFeature: 'income_cap', fullOnly: true },
      // v0.4 使い勝手フィードバック⑤ (D-U7): 自分のスキル・人材像確認 (読み取り専用)。
      // requiredTenantFeature 不要 (routes/employee-skills.ts に tenant_features ゲートは無い)。
      { to: '/me/skills', label: '自分のスキル・人材像', description: '登録されているスキル・人材像を確認 (読み取り専用)', icon: GraduationCap, requiredFeature: 'skill', fullOnly: true },
      // v0.4 休暇管理 (TD-26) §6-1 L-5: 家族情報。⚠️ 要配慮個人情報 (要介護状態) を扱うため、
      // 入力欄は同意チェックを通るまで DOM に出さない (D-80、画面側の実装)。
      // ⚠️ requiredTenantFeature 必須 — routes/employee-family.ts の全 sub-app が
      // requireFamilyFeatureEnabled で 403 にするため、付けないとフラグ OFF テナントで dead-end (U-12)。
      {
        to: '/me/family', label: '家族情報', description: '扶養・介護休業等の判定に使う家族の情報を登録',
        icon: UsersRound, requiredFeature: 'employee_family', requiredTenantFeature: 'family_member_management', fullOnly: true,
      },
      // v0.4 追加要件⑰: 社内文書ライブラリ。⚠️ 全従業員が読む機能なので fullOnly を付けない
      // (両 edition 対応)。⚠️ requiredTenantFeature を必ず付けること — permission 用の
      // requiredFeature だけではフラグ OFF テナントで常時表示され開くと 403 の dead-end になる
      // (security-reviewer M-5、追加要件⑪ 章③ U-12 と同型)。
      // ⚠️ requiresNonSelfScope は付けない — document permission に self scope は存在しない (D-489)。
      {
        to: '/documents', label: '社内文書', description: '就業規則・各種規程などの閲覧',
        icon: BookOpen, requiredFeature: 'document', requiredTenantFeature: 'document_library',
      },
      // TD-28 段階④ (D-5/D-10) + §14 手順8 (D-9): 汎用申請。category ごとに permission feature が
      // 分かれるため requiredAnyFeature で判定する。カットオーバー完了により休暇 (leave) と
      // 家族情報 (employee_family) もこの画面へ統合された。HomePage.tsx と同時更新 (§8-55対策)
      {
        to: '/requests', label: '申請', description: '休暇・勤務調整・個人情報変更などの申請の作成と確認', icon: FileText, fullOnly: true,
        requiredAnyFeature: ['leave', 'work_adjustment', 'personal_info_change', 'request_instance_other', 'employee_family'],
      },
      // TD-28 §18-3: 休暇の実績 (残数・年5日・有休管理簿)。申請は上の「申請」画面へ移った。
      // requiredFeature='leave' は migration 0080 で R-MANAGER (own_store) / R-STAFF (self) の
      // 両方に view scope が付与済みのため、店長・パート双方に表示される
      // (§8-50 型の scope 取り違えに注意、表示可否は feature の保有有無のみ)
      // ⚠️ requiredTenantFeature='leave_management' は休暇管理フロントエンド実装時に追加した。
      // routes/leave.ts / leave-grants.ts は requireLeaveFeatureEnabled で 403 を返すのに nav 側に
      // ゲートが無く、フラグ OFF のテナントでメニューが常時表示され開くと dead-end になっていた (U-12)。
      { to: '/leave/records', label: '休暇の残数と記録', description: '休暇の残数・年5日取得義務・有休管理簿の確認', icon: Umbrella, requiredFeature: 'leave', requiredTenantFeature: 'leave_management', fullOnly: true },
    ],
  },
  {
    label: 'ダッシュボード', key: 'dashboard', showInHome: true,
    items: [
      { to: '/dashboard', label: '全社サマリ', description: '会社全体の主要 KPI を確認', icon: LayoutDashboard, dynamicRootLabel: true, requiredFeature: 'dashboard' },
      { to: '/dashboard/trends', label: 'KPI推移', description: '月別・週別の指標を時系列で確認', icon: TrendingUp, requiredFeature: 'dashboard', fullOnly: true },
      { to: '/dashboard/comparison', label: '店舗比較', description: '複数店舗の指標を横並びで比較', icon: GitCompare, requiredFeature: 'dashboard', fullOnly: true },
      // v0.4 追加要件⑭ (D-466②): 計画・実績対比。両 edition 対応 (D-414) のため fullOnly を付けない。
      // ⚠️ requiredFeature は API が実際に要求する 'kpi_target' と1対1にすること
      // (U-12: 'dashboard' 一律にすると kpi_target を持たないユーザーにメニューは見えるが開くと403になる)
      { to: '/dashboard/targets', label: '目標と実績', description: '年度・四半期累計での達成状況と乖離を確認', icon: Target, requiredFeature: 'kpi_target' },
      // v0.5 日報売上進捗ダッシュボード (参考値、docs/design/v05-daily-report-sales-progress-dashboard.md):
      // API 側 (kpi_target:view 相乗り) と requiredFeature を一致させる。Full限定 (fullOnly)。
      {
        to: '/dashboard/daily-report-sales-progress', label: '日報売上の進捗（参考値）',
        description: '日報の店舗売上をもとに、当月目標に対する進捗を店舗別・合算で確認',
        icon: Newspaper, requiredFeature: 'kpi_target', fullOnly: true,
      },
    ],
  },
  {
    label: 'シフト運用', labelKey: 'nav.shift', key: 'shift', showInHome: true,
    items: [
      { to: '/shift/periods', label: 'シフト期間', description: '店舗ごとのシフト案作成の進捗を確認', icon: CalendarDays, requiredFeature: 'shift_periods', fullOnly: true },
      // v0.4 章④ F-3 (§0-J-3、D-106): 恒常パターン調整。HomePage.tsx のカードも同時更新 (§8-55対策)
      { to: '/shift/standing-patterns', label: '恒常パターン調整', description: '店舗の週間カバレッジを見ながら基本の働き方を調整', icon: Repeat, requiredFeature: 'shift_preference', fullOnly: true },
      // v0.4 追加要件⑮ 実装順序⑥ (旧 /shift/adjustments を改称): 勤務実績。予定に無い勤務も記録できる
      { to: '/shift/records', label: '勤務実績', description: 'シフト通りと違った勤務や予定に無い勤務があった枠だけ記録', icon: ClipboardCheck, requiredFeature: 'shift_records', fullOnly: true },
      // v0.4後半 月次実績一括入力: シフト予定を組まず月次でまとめて記録・提出する代替導線
      { to: '/shift/monthly-entry', label: '月次実績一括入力', description: '紙の出勤表のように、月次でまとめて勤務実績を入力・提出', icon: CalendarDays, requiredFeature: 'shift_monthly_entry', fullOnly: true },
      // v0.4後半 月次実績一括入力 第2フェーズ I-5 (§7-5): 承認一覧。/approvals と同じ理由
      // (D-212のコメント参照) で requiredFeature は付けない — 承認できるかは条件セット (テナント設定)
      // 次第であり、静的な feature/permission だけでは判定できない (GET /approval-queue/eligibility
      // が実際の判定を行う、開いても空一覧なら実害無し)。
      { to: '/shift/work-record-approvals', label: '勤務実績の承認', description: '月次で提出された勤務実績を人ごとにまとめて承認・差し戻し', icon: CheckCircle2, fullOnly: true },
      // v0.4後半 月次実績一括入力 第2フェーズ I-6 (§8-5): 給与計算等への出力設定・実行。
      // requiredTenantFeature は既存の月次実績機能フラグを共用する (バックエンドと同じ、§8参照)。
      // v0.4後半 月次実績一括入力 第2フェーズ I-6b (§9-0/§9-1、D-1028): 管理部門向けの確認画面。
      // 出力設定 (attendance_export) より先に一覧・確認したい導線のため、出力設定の直前に置く。
      {
        to: '/shift/attendance-review', label: '勤怠実績の確認', description: '提出・承認済みの勤務実績を人別・店舗別に確認し、出力設定へ進む', icon: ListChecks,
        requiredFeature: 'attendance_review', requiredTenantFeature: 'attendance_monthly_v2', fullOnly: true,
      },
      {
        to: '/shift/attendance-exports', label: '勤怠実績の出力', description: '給与計算等に渡すCSV/Excel出力の項目・列順を設定し、期間・店舗を指定して実行', icon: FileSpreadsheet,
        requiredFeature: 'attendance_export', requiredTenantFeature: 'attendance_monthly_v2', fullOnly: true,
      },
      { to: '/shift/urgent', label: '急な出勤要請', description: '当日欠勤等による欠員を店舗内・近隣店舗へ知らせ応援を募る', icon: AlertTriangle, requiredFeature: 'shift_urgent', fullOnly: true },
      // v0.4 F-6 (§0-J-2 項目4、D-78): 印刷。HomePage.tsx のカードも同時更新 (§8-55対策)
      { to: '/shift/print', label: 'シフト印刷', description: '確定したシフトを掲示・配布用に印刷', icon: Printer, requiredFeature: 'shift_print', fullOnly: true },
      // v0.4 追加要件⑪ 章③ (D-355): 日別サマリ。requiredFeature は API と1対1対応させる
      // (U-12: shift 一律にすると shift_report OFF のテナントでメニューは見えるが開くと403になる)。
      // HomePage.tsx のカードも同時更新 (§8-55対策)
      {
        to: '/shift/report/daily', label: '日別サマリ', description: '勤務予定・実績を店舗×日で横断確認', icon: Table2,
        requiredFeature: 'shift_report_daily', requiresNonSelfScope: true, requiredTenantFeature: 'shift_report', fullOnly: true,
      },
      // v0.4 追加要件⑪ 章⑤ (D-365): 従業員横断ビュー。HomePage.tsx のカードも同時更新 (§8-55対策)
      {
        to: '/shift/report/employees', label: '従業員横断ビュー', description: '勤務予定・実績を従業員×日で横断確認 (複数店舗・月またぎ)', icon: UserSearch,
        requiredFeature: 'shift_report_employees', requiredTenantFeature: 'shift_report', fullOnly: true,
      },
    ],
  },
  {
    label: '労務', key: 'labor', showInHome: true,
    items: [
      // v0.4 追加要件⑪ 章⑥ (D-349): 36協定 遵守状況。requiredTenantFeature は overtime_compliance
      // (shift_report とは別フラグ、U-12)。HomePage.tsx のカードも同時更新 (§8-55対策)
      {
        to: '/shift/report/compliance', label: '36協定 遵守状況', description: '法定外労働時間を算出し36協定の上限と対比 (賃金の計算はしません)', icon: Scale,
        requiredFeature: 'shift_report_compliance', requiredTenantFeature: 'overtime_compliance', fullOnly: true,
      },
      // v0.4 休暇管理 (TD-26) 段階⑥ (§6-1 L-4): 長期休業の管理。leave:view/manage が無ければ
      // canShowNavItem がそもそも表示しない (requiredFeature='leave' の保有チェックのみ)
      { to: '/leave/absences', label: '長期休業の管理', description: '産休・育休・介護休業の期間計算・確定・分割・延長', icon: HeartPulse, requiredFeature: 'leave', requiredTenantFeature: 'leave_management', fullOnly: true },
      // v0.4 休暇管理 (TD-26) §6-1 L-2: 付与の実行・調整・取消。⚠️ requiredAction='manage' は推測ではなく
      // 実測: routes/leave-grants.ts の requireTenantWideGrantManage が全操作で leave_grant:manage を要求し、
      // `permissions` に leave_grant:manage は all_stores 行しか無い (migration 0076) ため、
      // feature+action の保有だけで「テナント全体の付与を管理できる人」と判定してよい (D-294/H-4)。
      // ⇒ requiresNonSelfScope は不要 (self 行が構造的に存在しない)。
      {
        to: '/leave/grants', label: '休暇の付与', description: '有休の一括付与・手動付与・残数の調整・取消',
        icon: CalendarPlus, requiredFeature: 'leave_grant', requiredAction: 'manage',
        requiredTenantFeature: 'leave_management', fullOnly: true,
      },
      // v0.4 F-5 (§0-J-2、D-212): 承認受信箱。
      // ⚠️ nav item単位の権限粒度化(論点B、shift分割、§3-8)で修正(2026-09-10、5並列レビューCRITICAL指摘):
      // 従来ここは`requiredFeature: 'shift'`だったが、実際のAPI側`GET /approvals`(routes/approvals.ts)は
      // featureチェックを一切行っておらず、「自分がそのpending申請の承認者に割当てられているか」という
      // データレベルの自己判定のみで結果を絞り込む設計(company_adminのfeatureワイルドカード以外に
      // `approval_workflow`は誰にも付与されていない)。一度`requiredFeature: 'approval_workflow'`に
      // 修正したところ、実際の承認者(店長等)がnavから「承認」項目を失うという新たな不整合を生んだため、
      // requiredFeatureを完全に外す。中身がAPI側で自己スコープされているため、誰でも開けてよい
      // (該当申請が無ければ空一覧が表示されるだけで実害無し)。
      { to: '/approvals', label: '承認', description: '自分が承認者になっている申請の確認・判断', icon: CheckCircle2, fullOnly: true },
    ],
  },
  {
    // v0.4 使い勝手フィードバック⑤ (D-U7、Q-U8/U9b user回答2026-09-04):
    // 「自分のスキル確認」は既存の「マイページ」グループへ追加、「配下メンバーのスキル確認」は
    // 独立グループへ分割。requiresNonSelfScope により self scope しか持たない一般従業員には
    // このグループ自体が表示されない (routes/talent-skills.ts が同スコープを403にする実装と対応)。
    label: 'タレントマネジメント', key: 'talent', showInHome: true,
    items: [
      // v0.5 人材育成ダッシュボード: 旧「配下メンバーのスキル確認」「認定の確定・インポート」
      // 「人材像マトリクス」「育成計画」の4画面をタブ統合 (docs/design/v05-talent-development-dashboard-detail.md §0-2/§2)。
      // ゲートは旧「認定の確定・インポート」「人材像マトリクス」と同一 (requiresNonSelfScope +
      // requiredTenantFeature='competency_management')。旧「育成計画」は requiresNonSelfScope を
      // 付けていなかった (self scope の一般スタッフにも「自分の育成計画」を見せていた) が、
      // 統合後は `/me/skills` の育成計画セクションが本人分の閲覧を引き続き担う (§0-2 参照)。
      {
        to: '/talent/dashboard', label: '人材育成ダッシュボード',
        description: '配下メンバーの人材像充足状況・育成計画をまとめて確認',
        icon: Users2, requiredFeature: 'skill', requiresNonSelfScope: true,
        requiredTenantFeature: 'competency_management', fullOnly: true,
      },
      // 人・組織機能 増分③ (SC-07、D-607): 配置制約の登録・一覧。⚠️ skill/certification/matrixとは異なり
      // requiresNonSelfScope を付けない — R-STAFF は placement_constraint:view:self を実際に grant
      // されており (Q-329)、GET /placement-constraints は requirePersonScope 経由で self を許可する
      // 設計のため、自己スコープの一般スタッフにも「自分の配置制約」を見せる (作成・編集はできない)。
      {
        to: '/talent/placement-constraints', label: '配置制約', description: '時間・場所・業務の配置制約と通勤情報を登録・確認 (人×店舗の配置可否マトリクスを含む)',
        icon: MapPinned, requiredFeature: 'placement_constraint', requiredTenantFeature: 'placement_constraint', fullOnly: true,
      },
      // 人・組織機能 増分⑤ (SC-06、D-636): 充足状況ビュー (店舗×面、月次会議の主画面)。
      // GET /competency/coverage は /competency/matrix と同じノード依存ゲート (isResourceInPermissionScope)
      // で self scope の分岐を持たないため、requiresNonSelfScope を付ける (人材像マトリクスと同一判断)。
      {
        to: '/talent/coverage', label: '充足状況ビュー', description: '店舗ごとの面 (業務×抽象度) の充足人数を確認',
        icon: Gauge, requiredFeature: 'skill', requiresNonSelfScope: true, requiredTenantFeature: 'competency_management', fullOnly: true,
      },
      // 人・組織機能 増分⑤ (SC-09、UC-14): 配置シミュレーション。placement_simulation permission は
      // R-COMPANY(all_stores)/R-MANAGER(own_store) にのみ付与され self scope の行が存在しない
      // (migration 0141) ため、requiresNonSelfScope は不要 (self scope しか無いユーザーは
      // hasPermission が既に false を返す)。
      {
        to: '/talent/simulation', label: '配置シミュレーション', description: '抜き取り候補の配置可否と抜き取り元への影響を確認',
        icon: FlaskConical, requiredFeature: 'placement_simulation', requiredTenantFeature: 'placement_constraint', fullOnly: true,
      },
    ],
  },
  {
    label: 'データ入力', key: 'data', showInHome: true,
    items: [
      { to: '/imports/data', label: 'データ取込', description: 'CSV から売上・納品・諸経費などを取込', icon: Upload, admin_required: true, requiredFeature: 'import', requiredAction: 'manage', fullOnly: true },
      // v0.3 (S-10): AI-OCRのバックエンド認可がisCompanyAdmin固定からai_ocr permissionベースに
      // 置き換わったため (docs/design/v03-data-publish-gate-detail.md §8)、サイドバーもそれに
      // 追従する (§8-55: navGroups変更時はHomePage.tsxのmenuCardsも必ず同時更新すること)
      // v0.3 (S-6.5デザインレビュー反映): 「(納品書取込)」の説明書き込みで13文字になり2行化していたため
      // サイドバーでは省略 (説明はHomePage.tsxのカードdescriptionで別途表示、そちらは幅に余裕がある)
      { to: '/ai-ocr', label: 'AI-OCR', description: '納品書PDFをAIが読み取り、内容を確認してから取込', icon: FileScan, admin_required: true, requiredFeature: 'ai_ocr', fullOnly: true },
      // v1.0 (Phase 3 Sprint 10): Lite版 月次PL/仕入先別コスト CSV取込
      { to: '/imports/lite', label: 'Lite版取込', description: 'CSV から月次PL・仕入先別コストを取込 (Lite版データモデル)', icon: FileSpreadsheet, admin_required: true, requiredFeature: 'import', liteOnly: true },
      // 【2026-08-06 fullOnly追加】data-browse.ts が sales_details 等Full専用テーブルを直接SQLで
      // JOINしており (edition分岐なし)、Liteテナントで開くと即500になることが実機検証で発覚
      // v0.3.x レコード単位データ編集: 両edition対応。権限ゲート新設に伴い requiredFeature を付与
      // (HomePage.tsx のカードと同時更新、§8-55対策)
      { to: '/data', label: '取込済みデータ', description: '取込済みデータの確認と、行ごとの追加・修正・削除', icon: Database, requiredFeature: 'import' },
      // v0.3 (S-15設計乖離メモ§8a item3): labor/opex/delivery_salesはentry_scope=narrow非対応のため
      // import:manageと同じfeatureでゲートされる (manual_entry_*の個別featureはsales/delivery_detailsのみ)
      { to: '/entry/labor', label: '人件費', description: '店舗・月ごとの人件費を画面から直接入力', icon: Wallet, admin_required: true, requiredFeature: 'import', requiredAction: 'manage', fullOnly: true },
      { to: '/entry/opex', label: '諸経費', description: '賃料・電気・ガス等の諸経費を項目別に入力', icon: Receipt, admin_required: true, requiredFeature: 'import', requiredAction: 'manage', fullOnly: true },
      // v0.3 (S-6.5デザインレビュー反映): デリバリー売上(出荷)とTruck(納品/入荷)のアイコンが
      // 逆に見える指摘のため、Bike(出荷)/PackageCheck(入荷)に入れ替え
      { to: '/entry/delivery-sales', label: 'デリバリー売上', description: 'デリバリーサービス別の月次売上を入力', icon: Bike, admin_required: true, requiredFeature: 'import', requiredAction: 'manage', fullOnly: true },
      { to: '/entry/sales', label: '売上明細', description: '店舗・メニューごとの売上明細を画面から直接入力', icon: ClipboardList, admin_required: true, requiredFeature: 'manual_entry_sales', requiredAction: 'create', fullOnly: true },
      { to: '/entry/delivery-details', label: '納品明細', description: '店舗・業者ごとの納品明細を画面から直接入力', icon: PackageCheck, admin_required: true, requiredFeature: 'manual_entry_delivery_details', requiredAction: 'create', fullOnly: true },
      // v0.3 (来客数の汎用化): visitor_counts は import:manage permission に相乗り (labor/opex と同型)
      { to: '/entry/visitor-counts', label: '来客数', description: '店舗・月ごとの来客数を画面から直接入力', icon: Users, admin_required: true, requiredFeature: 'import', requiredAction: 'manage', fullOnly: true },
      // v1.0 (Lite版データ手入力、2026-08-06): CSV取込を経由しない月次PL/仕入先別コスト/枠外調整の直接入力
      { to: '/entry/lite', label: 'データ手入力', description: '月次PL・仕入先別コスト・枠外調整をCSVを介さず画面から直接入力', icon: PenLine, admin_required: true, requiredFeature: 'import', requiredAction: 'manage', liteOnly: true },
      // v0.3 (S-7データ公開ゲート): publishのview権限 + テナントfeature flag両方が必要
      { to: '/publish-management', label: 'データ公開管理', description: '店舗別データの入力完了・公開状態を管理', icon: CheckCircle2, requiredFeature: 'publish', requiredTenantFeature: 'publish_gate_enabled' },
    ],
  },
  {
    label: 'マスタ', key: 'master', showInHome: true,
    items: [
      { to: '/imports/master', label: 'マスタ取込', description: 'CSV で店舗・メニュー・食材・KPI閾値などを一括取込', icon: DatabaseBackup, admin_required: true, requiredFeature: 'import', requiredAction: 'manage', fullOnly: true },
      // v0.3 (S-17 staging E2E S15で発覚): store(node)/menu_categoryはCRUD粒度permissionの対象外
      // (S-15スコープ8ファイルに含まれず、apps/api/src/routes/nodes.ts・menu-categories.tsは
      // 引き続きisCompanyAdmin直接判定のまま)。requiredFeature未定義のままだとadmin_required も
      // 無いため誰でもサイドバーに表示されてしまい、非adminがクリックすると編集ボタンが
      // (ページ側に権限分岐が無いため) 常に表示された上で実際の操作は403になる不整合があった。
      // 実際のバックエンドゲートに合わせ admin_required:true で統一する
      // v0.3 (S-6.5デザインレビュー反映): 「マスタ」グループ配下で全項目がラベル末尾に「マスタ」を
      // 重複表記しており(「デリバリー業者マスタ」等10文字超で2行化リスク)、bugfix-03の
      // 「ラベルからグループ名の重複を削る」原則を同グループの残り項目にも適用
      { to: '/masters/stores', label: '店舗', description: '店舗情報の登録・編集', icon: Building2, admin_required: true },
      // 【2026-08-06 fullOnly追加】menu_master/menu_categoriesはjournal.menu_breakdown機能ON時のみ
      // Liteでも使用可 (Chapter 1.2 分類E) だが、当該feature flagが未実装のため現時点では一律非表示。
      // journal機能実装時に tenant_features 判定へ差し替えること
      { to: '/masters/menus', label: 'メニュー', description: '商品メニュー・単価の管理', icon: UtensilsCrossed, requiredFeature: 'menu', fullOnly: true },
      { to: '/masters/menu-categories', label: 'カテゴリ', description: 'メニューのカテゴリ分類を管理', icon: FolderTree, admin_required: true, fullOnly: true },
      { to: '/masters/delivery-services', label: 'デリバリー業者', description: '契約デリバリー業者と店舗別利用履歴', icon: Truck, requiredFeature: 'delivery_service', fullOnly: true },
      { to: '/masters/ingredients', label: '食材', description: '食材コード・単価等の登録・編集', icon: Wheat, requiredFeature: 'ingredient', fullOnly: true },
      { to: '/masters/kpi-thresholds', label: 'KPI閾値', description: '警告閾値を設定して異常を検知', icon: AlertTriangle, requiredFeature: 'kpi_threshold' },
      // v0.4 追加要件⑭ (D-466①): 売上目標の登録。書込み専用の画面のため requiredAction='manage'
      // (D-440: view で出すと「開けるが保存できない」dead-end になる。API も manage を要求する)
      { to: '/masters/kpi-targets', label: '売上目標', description: '年度・四半期・月次の売上目標を登録', icon: Target, requiredFeature: 'kpi_target', requiredAction: 'manage' },
      // v0.5① 日報機能 増分1 (D-837): 金種マスタ。両edition対応 (denomination-masters.ts は
      // editionScope='both')。⚠️ daily_report は tenant_features に未登録 (未実装) のため
      // requiredTenantFeature は付けない — requiredFeature (permission) のみで判定する。
      { to: '/masters/denominations', label: '金種（現金カウント）', description: '現金カウント・金種明細で使う額面・棒金単位の登録', icon: Coins, requiredFeature: 'daily_report_config' },
      // v0.4 章④ F-3 (§0-J-3): スキル・人材像・役職 / イベント。Full 限定 (D-1)
      // ⚠️ 人・組織機能 増分② (D-608): 業務/尺度/前提関係/上書きタブ・セクションは competency_management
      // フラグの機能だが、既存タブ (スキル/習熟度/人材像/役職) は同フラグ以前から存在する。フラグOFFのテナントは
      // 増分②拡張タブが露出しないよう、画面全体をフラグで隠す (requiredTenantFeature を新設)。
      { to: '/masters/skills', label: 'スキル・人材像', description: 'できる業務 (スキル)・習熟度・人材像・役職の登録', icon: GraduationCap, requiredFeature: 'skill', requiredTenantFeature: 'competency_management', fullOnly: true },
      { to: '/masters/store-events', label: 'イベント', description: '祭り等、需要が普段と変わるイベントの登録', icon: PartyPopper, requiredFeature: 'staffing', fullOnly: true },
      // v0.4後半 月次実績一括入力: attendance_reason_codes の管理画面 (CRUD APIはD-112から存在したが
      // 画面がゼロだった)。月次実績一括入力の区分選択肢もここで管理する。
      { to: '/masters/attendance-reason-codes', label: '勤怠区分', description: '遅刻・早退・欠勤・休暇・休日出勤等の理由コードの登録', icon: ClipboardCheck, requiredFeature: 'shift_attendance_reason_master', fullOnly: true },
      // v0.4後半 月次実績一括入力 第2フェーズ I-1b (§3-2/§3-3): グレード・契約区分マスタ。
      // 'skill' feature を再利用する (employee-org-masters.ts 側の判断理由を参照)。competency_management
      // フラグとは無関係のため requiredTenantFeature は付けない (skills 行との違い)。
      // design-expertレビュー Minor対応: IdCard/CalendarClockは既に社員管理/シフト希望で使用中のため
      // 別アイコンにする (アイコンによる視認性を損なわないため)
      { to: '/masters/employee-grades', label: 'グレード', description: '社員に設定するグレード（等級）の登録', icon: Award, requiredFeature: 'skill', fullOnly: true },
      { to: '/masters/working-time-contract-classes', label: '契約区分', description: '所定労働時間・日数の標準値（契約区分）の登録', icon: Hourglass, requiredFeature: 'skill', fullOnly: true },
    ],
  },
  {
    label: 'メニュー開発', key: 'menu_dev', showInHome: true,
    items: [
      // v0.3 (S-10): AIメニュー開発/シグネチャプロファイルのバックエンド認可がpermissionベースに
      // 置き換わったため、サイドバーもそれに追従する (§8-55対策、HomePage.tsxと同時更新)
      { to: '/ai/menu-chat', label: 'AIメニュー開発', description: '新メニュー提案・原価シミュ・既存メニュー改良をAIに相談', icon: Sparkles, admin_required: true, requiredFeature: 'ai_menu_chat', fullOnly: true },
      { to: '/ai/menu-favorites', label: 'お気に入り', description: 'AIが提案したメニューの一覧・試作フィードバック記録', icon: Star, admin_required: true, requiredFeature: 'ai_menu_favorites', fullOnly: true },
      { to: '/ai/menu-constraints', label: '制約事項', description: 'AIが必ず守るべき食材制限・予算・コンセプト等を登録', icon: ShieldAlert, admin_required: true, requiredFeature: 'ai_menu_constraints', fullOnly: true },
      { to: '/ai/menu-profiles', label: 'シグネチャ商品', description: 'スープ・タレ・香味油・主食材等の組合せを店舗ごとに登録', icon: ChefHat, admin_required: true, requiredFeature: 'signature_profile', fullOnly: true },
    ],
  },
  {
    // v0.5⑧ 経営会議PDCA (D-912): 「会議」と「施策台帳」を1つの機能として見せるため独立グループにする
    // (dashboard グループに混ぜない)。担当者 (board_initiative:self のみ) には「施策台帳」だけ出る。
    label: '経営会議', key: 'board', showInHome: true,
    items: [
      {
        to: '/board-meetings', label: '会議', description: '月次の経営会議とAI生成ブリーフ',
        icon: CalendarCheck, requiredFeature: 'board_meeting', requiredTenantFeature: 'board_meeting',
      },
      {
        to: '/board-initiatives', label: '施策台帳', description: '会議で決めた打ち手の進捗と効果測定',
        icon: Rocket, requiredFeature: 'board_initiative', requiredTenantFeature: 'board_meeting',
      },
    ],
  },
  {
    label: '管理', key: 'admin', showInHome: true,
    items: [
      { to: '/users', label: 'ユーザー管理', description: 'ユーザーの追加・編集・権限付与', icon: Users, admin_required: true, requiredFeature: 'user', requiresNonSelfScope: true },
      { to: '/employees', label: '社員管理', description: '社員の氏名・区分・雇用形態を登録（承認者の登録に必要）', icon: IdCard, admin_required: true },
      { to: '/roles', label: 'ロール・権限', description: 'ロール定義と権限マトリクスの閲覧', icon: ShieldCheck, requiredFeature: 'role' },
      // v1.0 (2026-08-06): v0.1由来のaudit_log permission (company_adminロールに割当済み) に
      // 対応する画面が無かったギャップを埋める新規追加。GET /platform/audit-logs (システム管理者専用、
      // 別のfeature) とは無関係
      { to: '/audit-logs', label: '監査ログ', description: 'マスタ編集・データ取込等の操作履歴を確認', icon: History, requiredFeature: 'audit_logs' },
      // v0.5 AIメニュー開発の利用ランキング (D-976): コンフィグの一タブから切り出した独立画面。
      // requiredTenantFeature を必ず付ける (§8-152型: フラグOFFテナントで項目だけ表示され
      // 開くと403のdead-endになるのを避ける)。既存の ai_usage_management フラグに相乗りする
      // (新しい tenant_features フラグは作らない、design §3-5)。
      {
        to: '/ai-menu-usage', label: 'AIメニュー利用状況',
        description: '権限保有者ごとのAIメニュー開発の利用状況をランキングで確認',
        icon: Gauge, requiredFeature: 'ai_menu_usage_ranking', fullOnly: true,
        requiredTenantFeature: 'ai_usage_management',
      },
      // fix-sidebar-scroll.md 対処3: 「コンフィグ」は探してスクロールする対象ではなく常にそこに
      // あるものとして、サイドバーではスクロール領域の外・最下部に固定表示する (sidebarSlot='config')。
      // ホームでは「管理」グループ末尾の通常カードとして出す。
      {
        to: '/settings', label: 'コンフィグ', description: '組織階層・承認ワークフロー・利用機能などの各種設定',
        icon: Settings, admin_required: true, sidebarSlot: 'config',
      },
    ],
  },
  {
    label: 'システム', key: 'system', showInHome: false,
    items: [
      { to: '/platform', label: 'システム管理', icon: ShieldCheck, platformAdminOnly: true, hideInHome: true },
    ],
  },
];

/** サイドバー最下部に固定表示する項目 (sidebarSlot='config')。ホームでは通常カードとして出る。 */
export const SIDEBAR_CONFIG_ENTRIES: NavEntry[] = NAV_GROUPS
  .flatMap((g) => g.items)
  .filter((e) => e.sidebarSlot === 'config');

/** サイドバーのグループ内に描画する項目 (sidebarSlot 指定のものを除く)。 */
export const sidebarItemsOf = (group: NavGroup): NavEntry[] => group.items.filter((e) => !e.sidebarSlot);

/** ホーム画面に出すカード。グループ順は NAV_GROUPS の順を踏襲する。 */
export const HOME_GROUPS: Array<{ key: HomeGroupKey; label: string; items: NavEntry[] }> = NAV_GROUPS
  .filter((g) => g.showInHome)
  .map((g) => ({
    key: g.key as HomeGroupKey,
    label: g.label,
    items: g.items.filter((e) => !e.hideInHome),
  }));
